import EmptyState from "@buildpy/ui/empty-state";
import ListTable, { type ListTableColumn } from "@buildpy/ui/list-table";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import { EMPTY_MARK, formatDate } from "@buildpy/core/lib/format";
import { Member, ROLE_LABEL } from "@buildpy/core/modules/org/types";
import type { SettingsView } from "@buildpy/core/views/g2c/settings";
import {
  InviteButton,
  InviteRowActions,
} from "@/modules/org/ui/settings-members-actions";

// 미수락 초대 한 줄 — 상태 문구·링크 경로는 설정 뷰가 정한다(경로는 대표에게만).
export type InviteRow = Extract<SettingsView, { tab: "members" }>["invites"][number];

interface Props {
  members: Member[];
  invites: InviteRow[];
  canEdit: boolean;
}

// 멤버 — 초대는 메일 없이 링크로 한다 · 실장은 읽기 전용 · 토큰은 대표에게만 내려간다.
const SettingsMembers = ({ members, invites, canEdit }: Props) => {
  const inviteColumns: ListTableColumn[] = [
    { label: "이메일", wrap: true },
    { label: "권한" },
    { label: "만료" },
    { label: "상태" },
    ...(canEdit ? [{ label: "링크", align: "right" } as const] : []),
  ];
  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex flex-col gap-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">
            멤버 {members.length}명
          </span>
          {canEdit && <InviteButton />}
        </div>

        <ListTable
          columns={[
            { label: "이름" },
            { label: "권한" },
            { label: "이메일", wrap: true },
            { label: "전화" },
            { label: "등록일", align: "right" },
          ]}>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="whitespace-nowrap px-3 py-2 font-medium">
                {member.name}
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-2">
                {ROLE_LABEL[member.role]}
              </TableCell>
              <TableCell className="px-3 py-2 text-muted-foreground">
                {member.email ?? EMPTY_MARK}
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                {member.phone ?? EMPTY_MARK}
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground">
                {formatDate(member.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </ListTable>
      </div>

      <div className="flex flex-col gap-y-3">
        <span className="text-sm text-muted-foreground">
          미수락 초대 {invites.length}건
        </span>

        {invites.length === 0 ? (
          <EmptyState>
            아직 보낸 초대가 없습니다.
          </EmptyState>
        ) : (
          <ListTable columns={inviteColumns}>
            {invites.map((invite) => (
              <TableRow key={invite.id}>
                <TableCell className="px-3 py-2 font-medium">
                  {invite.email}
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2">
                  {ROLE_LABEL[invite.role]}
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                  {formatDate(invite.expires_at)}
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {invite.status_label}
                </TableCell>
                {canEdit && (
                  <TableCell className="whitespace-nowrap px-3 py-2 text-right">
                    <InviteRowActions
                      inviteId={invite.id}
                      path={invite.path}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </ListTable>
        )}
      </div>
    </div>
  );
};

export default SettingsMembers;
